/**
 * CoverageBanner — alerts for lapsed department plans or payment issues.
 *
 * Renders ONLY when the officer doc signals a coverage problem:
 * - accessRestrictedReason === 'dept_lapsed' → department plan lapsed
 * - subscriptionStatus === 'past_due' → personal payment issue with grace
 *
 * Dismiss per-day via localStorage.
 */

import { useState } from 'react';
import { useSessionStore } from '../../stores';

const BANNER_KEY_PREFIX = 'copapp.coverageBannerDismissed';

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

interface OfficerCoverageFields {
  subscriptionStatus?: string | null;
  graceUntil?: string | null;
  accessRestricted?: boolean;
  accessRestrictedReason?: string | null;
}

export function CoverageBanner() {
  const { officer } = useSessionStore();
  const ext = officer as (typeof officer & OfficerCoverageFields) | null;

  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(BANNER_KEY_PREFIX) === todayKey();
    } catch {
      return false;
    }
  });

  if (!ext || dismissed) return null;

  const isDeptLapsed = ext.accessRestrictedReason === 'dept_lapsed';
  const isPastDue = ext.subscriptionStatus === 'past_due';

  if (!isDeptLapsed && !isPastDue) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(BANNER_KEY_PREFIX, todayKey());
    } catch {
      // localStorage may be unavailable
    }
    setDismissed(true);
  };

  const graceDate = ext.graceUntil
    ? new Date(ext.graceUntil).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[220px]">
        {isDeptLapsed ? (
          <>
            <p className="text-sm text-amber-400 font-medium">
              Your department&apos;s plan lapsed &mdash; training is paused.
            </p>
            <p className="text-xs text-amber-400/70 mt-0.5">
              Admins can fix it in Settings &rarr; Billing.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-amber-400 font-medium">
              Payment issue on your subscription &mdash; access continues
              {graceDate ? ` until ${graceDate}` : ' during the grace period'}.
            </p>
            <p className="text-xs text-amber-400/70 mt-0.5">
              Update your card in Settings.
            </p>
          </>
        )}
      </div>
      <button
        onClick={dismiss}
        className="px-2 py-1.5 text-xs text-amber-400/60 hover:text-amber-400 transition-colors"
        aria-label="Dismiss"
      >
        Dismiss
      </button>
    </div>
  );
}
